import { format } from "date-fns";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import { useGetMyAccounts, useGetTopSongs } from "../api/queries";
import useGetColours from "../helpers/getColours";
import SongDetails from "../screens/authenticated/dashboard/songs/SongDetails";
import ChooseAccountModal from "./ChooseAccountModal";
import LoadingSmall from "./LoadingSmall";
import RebrandButtonLink from "./RebrandButtonLink";
import { PlusIconMinus, PlusIconPlus } from "./svg/PlusIcon";
import Table from "./Table";

function ExpandCell({ row, isTunecore, highlightColour }) {
  return (
    <>
      {isTunecore ? (
        <span className="float-right">
          {row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      ) : (
        <span className="float-right">
          {row.isExpanded ? (
            <PlusIconMinus className="h-4" fill={highlightColour} />
          ) : (
            <PlusIconPlus className="h-4" fill={highlightColour} />
          )}
        </span>
      )}
    </>
  );
}

function renderExpandCell(props, isTunecore, highlightColour) {
  return <ExpandCell {...props} isTunecore={isTunecore} highlightColour={highlightColour} />;
}

function TopSongs() {
  const { t } = useTranslation("dashboard");
  const myAccounts = useGetMyAccounts();
  const topSongs = useGetTopSongs({ accounts: myAccounts?.data?.artistaccounts });
  const { highlightColour } = useGetColours();
  const [accountChoiceModal, setAccountChoiceModal] = useState(false);

  const isTunecore = useUIStateStore(state => state.isTunecore);

  const columns = React.useMemo(() => [
    {
      Header: t("dashboard:ra-column-title"),
      accessor: "title",
      width: "25%",
      id: "colTopSongTitle",
    },
    {
      Header: t("dashboard:ra-column-artist-account"),
      accessor: "accountname",
      width: "25%",
      id: "colTopSongAccount",
    },
    {
      Header: t("dashboard:ra-column-date-added"),
      accessor: "createddate",
      width: "15%",
      id: "colTopSongCreated",
      Cell: ({ row }) =>
        row?.values?.colTopSongCreated
        && row?.values?.colTopSongCreated !== "0000-00-00"
          ? format(Date.parse(row.values.colTopSongCreated), "yyyy-MM-dd")
          : null,
    },
    {
      Header: () => null,
      id: "expander",
      width: "5%",
      Cell: props => renderExpandCell(props, isTunecore, highlightColour),
      SubCell: () => null,
    },
  ].filter(Boolean), [t, isTunecore, highlightColour]);

  return (
    <>
      {!!accountChoiceModal && (
        <ChooseAccountModal
          title={t("dashboard:choose-account-songs-modal-header")}
          to="/songs/create"
          setAccountChoiceModal={setAccountChoiceModal}
          accounts={myAccounts?.data?.artistaccounts}
          nonAdminTo="/songs/"
        />
      )}
      <div
        className={`${
          isTunecore
            ? "bg-tunecore-dark-element text-white"
            : "bg-white text-black"
        } w-full p-8 pt-10 rounded-lg`}
      >
        <div className="flex flex-row items-center justify-between mb-5">
          <h2 className="text-xl font-medium ">
            {t("dashboard:recently-added-songs-header")}
          </h2>
          <RebrandButtonLink
            plusIcon
            id="btnTopSongAddSong"
            onClick={() => {
              setAccountChoiceModal(true);
            }}
          >
            {t("dashboard:add-new-song-link")}
          </RebrandButtonLink>
        </div>
        {topSongs.isLoading && <LoadingSmall />}
        {topSongs.isSuccess && (
          <div>
            <Table
              area="TopSongs"
              data={topSongs.data}
              min={5}
              columns={columns}
              highlightColour={highlightColour}
              revealComponent={<SongDetails />}
              searchSuffix={t("common:songs-search-suffix")}
              bgClass={`${
                isTunecore ? "bg-tunecore-dark-element" : "bg-white"
              } rounded-lg p-0`}
              initialSort={[
                {
                  id: "createddate",
                  desc: true,
                },
              ]}
            />
          </div>
        )}
        {topSongs.isError && (
          <div>
            <p>{t("dashboard:recent-songs-error")}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default TopSongs;
